import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

// Create and export fundamental scene objects
export const scene = new THREE.Scene();
export const camera = new THREE.PerspectiveCamera(
  75,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
);

// Set up renderer
export const renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: true,
  powerPreference: "high-performance",
  precision: "highp",
});

// Set up post-processing
export let composer: EffectComposer;

// Camera control constants
export const CAMERA_LERP_FACTOR = 0.03;
export const DIRECTION_CHANGE_THRESHOLD = 0.1;
export const cameraTargetPosition = new THREE.Vector3();
export const cameraTargetLookAt = new THREE.Vector3();
export const CAMERA_HEIGHT = 8;
export const CAMERA_DISTANCE = 8;

// Timing system
export const timing = {
  LOW: 5, // Update every 5 frames (~83ms at 60fps)
  MEDIUM: 3, // Update every 3 frames (~50ms at 60fps)
  HIGH: 1, // Update every frame (~16ms at 60fps)

  // Track last update times by priority
  lastUpdate: {
    LOW: 0,
    MEDIUM: 0,
    HIGH: 0,
  },

  // Check if we should update for this priority at this time
  shouldUpdate(priority: "LOW" | "MEDIUM" | "HIGH", time: number): boolean {
    const interval = this[priority];
    const frameNum = Math.floor(time / 16); // ~16ms per frame at 60fps

    if (frameNum % interval === 0) {
      this.lastUpdate[priority] = time;
      return true;
    }
    return false;
  },
};

// Initialize core systems
export function initCore(): void {
  // Setup scene
  scene.background = new THREE.Color(0x000022);
  scene.fog = new THREE.FogExp2(0x000033, 0.03);

  // Setup camera
  camera.position.set(0, 10, 10);
  camera.lookAt(0, 0, 0);

  // Setup renderer
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);

  // Setup post-processing
  initPostProcessing();

  // Basic lighting (moved from scene.ts)
  setupLighting();
}

function initPostProcessing(): void {
  composer = new EffectComposer(renderer);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  // Optimized bloom settings
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    0.4,
    0.5,
    0.8
  );
  bloomPass.threshold = 0.8;
  bloomPass.strength = 0.4;
  bloomPass.radius = 0.5;
  composer.addPass(bloomPass);

  const outputPass = new OutputPass();
  composer.addPass(outputPass);
}

function setupLighting(): void {
  // Main directional light - simplified from original
  const mainLight = new THREE.DirectionalLight(0x9999ff, 1);
  mainLight.position.set(5, 10, 7);
  mainLight.castShadow = true;
  mainLight.shadow.mapSize.width = 1024;
  mainLight.shadow.mapSize.height = 1024;
  scene.add(mainLight);

  // Ambient light
  const ambientLight = new THREE.AmbientLight(0x333366, 0.5);
  scene.add(ambientLight);

  // Hemisphere light for better ambient lighting
  const hemiLight = new THREE.HemisphereLight(0x7790ff, 0x444499, 0.6);
  scene.add(hemiLight);
}

// Handle window resize
export function handleResize(): void {
  const width = window.innerWidth;
  const height = window.innerHeight;

  camera.aspect = width / height;
  camera.updateProjectionMatrix();

  renderer.setSize(width, height);

  if (composer) {
    composer.setSize(width, height);
  }
}

// Update lights with optimization
export function updateLights(time: number): void {
  // Only update lights at MEDIUM priority (every 3 frames)
  if (!timing.shouldUpdate("MEDIUM", time)) return;

  const pointLights: THREE.PointLight[] = [];
  scene.traverse((object) => {
    if (object.type === "PointLight") {
      pointLights.push(object as THREE.PointLight);
    }
  });

  // Batch all light updates together to reduce calculations
  for (const light of pointLights) {
    if (light.userData.baseIntensity === undefined) {
      light.userData.baseIntensity = light.intensity;
    }

    const pulse = Math.sin(time * 0.001) * 0.15 + 1;
    light.intensity = light.userData.baseIntensity * pulse;
  }
}
